import React from 'react';
import { useProgram } from '../../contexts/ProgramContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { 
  Star, 
  Users, 
  Clock, 
  Award, 
  CheckCircle, 
  Play,
  BookOpen,
  Target,
  Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ProgramCard = ({ program, showProgress = false }) => {
  const { getUserProgress, enrollInProgram } = useProgram();
  const { convertPrice, formatPrice } = useCurrency();
  
  const userProgress = getUserProgress(program.id);
  const isEnrolled = userProgress.enrolled;

  const handleEnroll = () => {
    enrollInProgram(program.id);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Débutant':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Intermédiaire':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Avancé':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDuration = (duration) => {
    return duration;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow duration-200">
      {/* Image du programme */}
      <div className="relative">
        <img
          src={program.image}
          alt={program.title}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(program.level)}`}>
            {program.level}
          </span>
        </div>
        <div className="absolute top-3 right-3 bg-white dark:bg-gray-800 rounded-full px-2 py-1 flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Star className="w-3 h-3 text-yellow-400 fill-current" />
          {program.rating}
        </div>
        {isEnrolled && (
          <div className="absolute bottom-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
            Inscrit
          </div>
        )}
      </div>

      {/* Contenu du programme */}
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {program.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            {program.description}
          </p>
        </div>

        {/* Informations du programme */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Users className="w-4 h-4" />
            <span>{program.students.toLocaleString()} étudiants</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>{formatDuration(program.duration)}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <BookOpen className="w-4 h-4" />
            <span>{program.courses.length} cours</span>
          </div>
          
          {program.certificate && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Award className="w-4 h-4" />
              <span>Certificat inclus</span>
            </div>
          )}
        </div>

        {/* Prérequis */}
        {program.prerequisites && program.prerequisites.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Prérequis
            </h4>
            <div className="flex flex-wrap gap-1">
              {program.prerequisites.map((prereq, index) => (
                <span
                  key={index}
                  className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded"
                >
                  {prereq}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Compétences acquises */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            Compétences acquises
          </h4>
          <div className="flex flex-wrap gap-1">
            {program.skills.slice(0, 4).map((skill, index) => (
              <span
                key={index}
                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded"
              >
                {skill}
              </span>
            ))}
            {program.skills.length > 4 && (
              <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">
                +{program.skills.length - 4} autres
              </span>
            )}
          </div>
        </div>

        {/* Progression (si inscrit) */}
        {showProgress && isEnrolled && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Progression
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {userProgress.progress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${userProgress.progress}%` }}
              />
            </div>
            {userProgress.estimatedCompletion && (
              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                <Calendar className="w-3 h-3" />
                <span>Fin prévue: {new Date(userProgress.estimatedCompletion).toLocaleDateString('fr-FR')}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-primary dark:text-primary-400">
            {formatPrice(convertPrice(program.price))}
          </div>
          
          <div className="flex gap-2">
            <Link
              to={`/program/${program.id}`}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary-400 transition-colors"
            >
              <Target className="w-4 h-4" />
              Détails
            </Link>
            
            {isEnrolled ? (
              <Link
                to={`/program/${program.id}/learn`}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Play className="w-4 h-4" />
                Continuer
              </Link>
            ) : (
              <button
                onClick={handleEnroll}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                S'inscrire
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;
